import React, { useEffect } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';

import { AxiosError } from 'axios';

import { apiErrors, formErrors, isBadRequest } from 'utils/api/errors';
import { useStore } from 'utils/hooks/store';
import API from 'utils/api';
import Logger from 'utils/logger';

import { env } from 'config';

import { colors } from 'theme/colors';

import { LoginFormValues } from './types';
import { EnterEmailSchema } from './schema';

import EnterEmail from './components/EnterEmail';

import { FormContainer, LoginPageContainer, LoginPageWrapper } from './styles';

const Login: React.FunctionComponent = () => {
  useEffect(() => {
    document.body.style.backgroundColor = colors.common.white;
  }, []);

  const { notification } = useStore();

  const initialValues: LoginFormValues = {
    username: '',
  };

  const getFusionAuthClientId = async (username: string): Promise<string | undefined> => {
    const resp = await API.getFusionAuthApplicationData({ username })();
    const { applicationClientId } = resp.data;
    return applicationClientId;
  };

  const submitCredentials = async ({ username }: LoginFormValues): Promise<void> => {
    try {
      const fusionAuthApplicationId = await getFusionAuthClientId(username);
      if (!fusionAuthApplicationId) {
        notification.enqueueErrorSnackbar('We cannot log you in with that email address');
        return;
      }
      window.location.assign(`${env.REACT_APP_ADMIN_OIDC_URL}?username=${encodeURIComponent(username)}`);
    } catch (e) {
      const errors = apiErrors(e as AxiosError, 'You cannot log in right now');
      errors.forEach((message) => notification.enqueueErrorSnackbar(message));
    }
  };

  const onSubmit = async (values: LoginFormValues, helpers: FormikHelpers<LoginFormValues>): Promise<void> => {
    try {
      helpers.setSubmitting(true);
      await submitCredentials(values);
    } catch (e) {
      if (!isBadRequest(e as AxiosError)) {
        Logger.error('Unable to log in', e);
      }
      const { nonFieldErrors, fieldErrors } = formErrors(e as AxiosError);
      helpers.setSubmitting(false);
      if (fieldErrors) {
        helpers.setErrors(fieldErrors);
      }
      if (nonFieldErrors) {
        helpers.setStatus(nonFieldErrors);
      }
    }
  };

  return (
    <LoginPageWrapper>
      <LoginPageContainer>
        <FormContainer>
          <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={EnterEmailSchema}>
            {({ errors, touched }) => (
              <Form>
                <EnterEmail
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                />
              </Form>
            )}
          </Formik>
        </FormContainer>
      </LoginPageContainer>
    </LoginPageWrapper>
  );
};

export default Login;
